import React, { useState, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';

export interface DebugConsoleProps {
  logQuota?: number;
  serverUrl?: string;
  gameState?: any;
  showButton?: boolean;
}

export interface DebugLog {
  timestamp: string;
  message: string;
}

export const useDebugConsole = (props: DebugConsoleProps = {}) => {
  const {
    logQuota = 1000,
    serverUrl = '/api/save-logs',
    gameState,
    showButton = true
  } = props;

  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const [showDebugConsole, setShowDebugConsole] = useState(false);
  const originalConsoleRef = useRef<{
    log: typeof console.log;
    error: typeof console.error;
    warn: typeof console.warn;
  } | null>(null);

  const addDebugLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const newLog: DebugLog = { timestamp, message };
    
    setDebugLogs(prev => {
      const updatedLogs = [...prev, newLog];
      // Preserve logs up to quota limit instead of always limiting to 50
      return updatedLogs.length > logQuota ? updatedLogs.slice(-logQuota) : updatedLogs;
    });
  }, [logQuota]);

  const captureScreenshot = useCallback(async (serverUrl: string) => {
    try {
      addDebugLog('📸 Capturing screenshot...');
      
      const canvas = await html2canvas(document.body);
      const dataURL = canvas.toDataURL('image/png');
      
      // Try to send to server first
      try {
        const timestamp = new Date().toISOString();
        const deviceInfo = {
          userAgent: navigator.userAgent,
          timestamp: timestamp,
          url: window.location.href,
          type: 'screenshot'
        };

        const screenshotData = {
          timestamp,
          deviceInfo,
          screenshot: dataURL
        };

        addDebugLog('📤 Sending screenshot to server...');
        const response = await fetch(serverUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(screenshotData)
        });
        
        if (response.ok) {
          const result = await response.json();
          addDebugLog(`✅ Screenshot saved to server: ${result.filename}`);
          return;
        } else {
          throw new Error(`Server response: ${response.status}`);
        }
      } catch (serverError) {
        addDebugLog(`⚠️ Server upload failed: ${serverError}`);
        
        // Fallback: download locally
        const link = document.createElement('a');
        link.download = `vivu_screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        addDebugLog('📁 Screenshot downloaded to device as fallback');
      }
    } catch (error) {
      addDebugLog(`❌ Screenshot failed: ${error}`);
      addDebugLog('💡 Try using browser developer tools for screenshots');
    }
  }, [addDebugLog]);

  const sendLogsToServer = useCallback(async () => {
    try {
      const timestamp = new Date().toISOString();
      const deviceInfo = {
        userAgent: navigator.userAgent,
        timestamp: timestamp,
        url: window.location.href,
        gameState: gameState?.stage || 'unknown'
      };

      const logData = {
        timestamp,
        deviceInfo,
        logs: debugLogs.map(log => `[${log.timestamp}] ${log.message}`)
      };

      try {
        addDebugLog('📤 Sending logs to server...');
        addDebugLog(`🌐 Trying: ${serverUrl} (via proxy)`);
        
        const response = await fetch(serverUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logData)
        });
        
        if (response.ok) {
          const result = await response.json();
          addDebugLog(`✅ Logs saved to server: ${result.filename}`);
          return;
        } else {
          addDebugLog(`❌ Server responded with: ${response.status} ${response.statusText}`);
          throw new Error(`Server response: ${response.status}`);
        }
      } catch (serverError) {
        addDebugLog(`⚠️ Server upload failed: ${serverError}`);
        
        // Fallback: download as file
        const logContent = JSON.stringify(logData, null, 2);
        const blob = new Blob([logContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `vivu_debug_logs_${timestamp.replace(/[:.]/g, '-')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        addDebugLog('📁 Logs downloaded to device');
      }
    } catch (error) {
      addDebugLog(`❌ Failed to save logs: ${error}`);
    }
  }, [debugLogs, gameState, serverUrl, addDebugLog]);

  const setupConsoleOverride = useCallback(() => {
    if (originalConsoleRef.current) return; // Already set up

    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    
    originalConsoleRef.current = { log: originalLog, error: originalError, warn: originalWarn };
    
    console.log = (...args) => {
      originalLog(...args);
      const message = args.join(' ');
      // Only capture voice and speech related logs
      if (message.includes('🎤') || message.includes('Speech') || message.includes('Voice') || 
          message.includes('recognition') || message.includes('microphone') || message.includes('listen')) {
        addDebugLog(`LOG: ${message}`);
      }
    };
    
    console.error = (...args) => {
      originalError(...args);
      const message = args.join(' ');
      if (message.includes('🎤') || message.includes('Speech') || message.includes('Voice') || 
          message.includes('recognition') || message.includes('microphone') || message.includes('listen')) {
        addDebugLog(`ERROR: ${message}`);
      }
    };
    
    console.warn = (...args) => {
      originalWarn(...args);
      const message = args.join(' ');
      if (message.includes('🎤') || message.includes('Speech') || message.includes('Voice') || 
          message.includes('recognition') || message.includes('microphone') || message.includes('listen')) {
        addDebugLog(`WARN: ${message}`);
      }
    };
  }, [addDebugLog]);

  const cleanupConsoleOverride = useCallback(() => {
    if (originalConsoleRef.current) {
      console.log = originalConsoleRef.current.log;
      console.error = originalConsoleRef.current.error;
      console.warn = originalConsoleRef.current.warn;
      originalConsoleRef.current = null;
    }
  }, []);

  const clearLogs = useCallback(() => {
    setDebugLogs([]);
  }, []);

  const openLocalLogFolder = useCallback(async () => {
    try {
      addDebugLog('📂 Opening local log folder...');
      
      // Check if File System Access API is supported
      if ('showDirectoryPicker' in window) {
        try {
          addDebugLog('📁 Opening folder picker...');
          // @ts-ignore - File System Access API
          const directoryHandle = await window.showDirectoryPicker({
            mode: 'read'
          });
          
          addDebugLog('✅ Folder opened successfully');
          addDebugLog('💡 Look for files starting with "vivu_debug" or "vivu_screenshot"');
          
        } catch (error) {
          addDebugLog(`⚠️ Folder picker cancelled or failed: ${error}`);
          showFallbackInstructions();
        }
      } else {
        addDebugLog('⚠️ File System Access API not supported');
        showFallbackInstructions();
      }
    } catch (error) {
      addDebugLog(`❌ Error opening folder: ${error}`);
      showFallbackInstructions();
    }
  }, [addDebugLog]);

  const showFallbackInstructions = useCallback(() => {
    addDebugLog('💡 Manual access instructions:');
    addDebugLog('1. Open your Downloads folder manually');
    addDebugLog('2. Look for files starting with "vivu_debug" or "vivu_screenshot"');
    addDebugLog('📂 File patterns to find:');
    addDebugLog('   • vivu_debug_logs_*.json');
    addDebugLog('   • vivu_screenshot_*.png');
  }, [addDebugLog]);

  // Create a wrapper for captureScreenshot that passes the serverUrl
  const captureScreenshotWithUrl = useCallback(() => {
    return captureScreenshot(serverUrl);
  }, [captureScreenshot, serverUrl]);

  return {
    debugLogs,
    showDebugConsole,
    setShowDebugConsole,
    addDebugLog,
    sendLogsToServer,
    captureScreenshot: captureScreenshotWithUrl,
    setupConsoleOverride,
    cleanupConsoleOverride,
    clearLogs,
    openLocalLogFolder,
    showButton
  };
};

export interface DebugConsoleComponentProps {
  debugLogs: DebugLog[];
  showDebugConsole: boolean;
  onToggleConsole: (show: boolean) => void;
  onClearLogs: () => void;
  onUploadLogs: () => void;
  onCaptureScreenshot: () => void;
  onOpenLocalLogFolder: () => void;
  showButton?: boolean;
}

export const DebugConsole: React.FC<DebugConsoleComponentProps> = ({
  debugLogs,
  showDebugConsole,
  onToggleConsole,
  onClearLogs,
  onUploadLogs,
  onCaptureScreenshot,
  onOpenLocalLogFolder,
  showButton = true
}) => {
  return (
    <>
      {/* Debug Console */}
      {showDebugConsole && (
        <div style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          right: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: '#00ff00',
          fontFamily: 'monospace',
          fontSize: '12px',
          maxHeight: '200px',
          borderTop: '2px solid #333',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Fixed header with title and close button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '5px 10px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderBottom: '1px solid #333'
          }}>
            <strong>🐛 Debug Console ({debugLogs.length} logs)</strong>
            <button 
              onClick={() => onToggleConsole(false)}
              style={{
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                padding: '2px 6px',
                fontSize: '10px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
          
          {/* Fixed control buttons */}
          <div style={{ 
            padding: '5px 10px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderBottom: '1px solid #333'
          }}>
            <button 
              onClick={onClearLogs}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                fontSize: '10px',
                cursor: 'pointer',
                marginRight: '5px'
              }}
            >
              🗑️ Clear
            </button>
            <button 
              onClick={onUploadLogs}
              style={{
                backgroundColor: '#0066cc',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                fontSize: '10px',
                cursor: 'pointer',
                marginRight: '5px'
              }}
            >
              📤 Upload Logs
            </button>
            <button 
              onClick={onCaptureScreenshot}
              style={{
                backgroundColor: '#ff6600',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                fontSize: '10px',
                cursor: 'pointer',
                marginRight: '5px'
              }}
            >
              📸 Screenshot
            </button>
            <button 
              onClick={onOpenLocalLogFolder}
              style={{
                backgroundColor: '#9933cc',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                fontSize: '10px',
                cursor: 'pointer'
              }}
            >
              📂 Open Log Folder
            </button>
          </div>
          
          {/* Scrollable log display area */}
          <div style={{
            flex: 1,
            overflowY: 'scroll',
            padding: '5px 10px'
          }}>
            {debugLogs.slice(-20).map((log, index) => (
              <div key={index} style={{ marginBottom: '2px' }}>
                [{log.timestamp}] {log.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Debug Toggle Button */}
      {showButton && !showDebugConsole && (
        <button
          onClick={() => onToggleConsole(true)}
          style={{
            position: 'fixed',
            bottom: '10px',
            right: '10px',
            backgroundColor: '#333',
            color: '#00ff00',
            border: 'none',
            padding: '8px 12px',
            fontSize: '12px',
            cursor: 'pointer',
            borderRadius: '4px',
            zIndex: 9999
          }}
        >
          🐛 Debug
        </button>
      )}
    </>
  );
};